import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  CheckCircleIcon, 
  ExclamationTriangleIcon, 
  XCircleIcon, 
  ClockIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';

interface ServiceStatus {
  id: string;
  name: string;
  status: 'operational' | 'degraded' | 'outage' | 'maintenance';
  description: string;
  lastUpdated: string;
  uptime?: string;
}

interface Incident {
  id: string;
  title: string;
  status: 'investigating' | 'identified' | 'monitoring' | 'resolved';
  severity: 'minor' | 'major' | 'critical';
  description: string;
  startedAt: string;
  resolvedAt?: string;
  updates: IncidentUpdate[];
}

interface IncidentUpdate {
  id: string;
  message: string;
  timestamp: string;
  status: 'investigating' | 'identified' | 'monitoring' | 'resolved';
}

const StatusPage: React.FC = () => {
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(new Date());

  // Mock data - in a real implementation, this would come from an API
  const [services] = useState<ServiceStatus[]>([
    {
      id: 'api',
      name: 'API Services',
      status: 'operational',
      description: 'REST API and GraphQL endpoints',
      lastUpdated: '2024-01-15T10:30:00Z',
      uptime: '99.9%'
    },
    {
      id: 'database',
      name: 'Database',
      status: 'operational',
      description: 'Primary and replica databases',
      lastUpdated: '2024-01-15T10:30:00Z',
      uptime: '99.95%'
    },
    {
      id: 'auth',
      name: 'Authentication',
      status: 'operational',
      description: 'User authentication and authorization',
      lastUpdated: '2024-01-15T10:30:00Z',
      uptime: '99.8%'
    },
    {
      id: 'payments',
      name: 'Payment Processing',
      status: 'operational',
      description: 'Stripe payment processing',
      lastUpdated: '2024-01-15T10:30:00Z',
      uptime: '99.9%'
    },
    {
      id: 'analytics',
      name: 'Analytics Engine',
      status: 'operational',
      description: 'Trend analysis and ML processing',
      lastUpdated: '2024-01-15T10:30:00Z',
      uptime: '99.7%'
    },
    {
      id: 'cdn',
      name: 'CDN & Static Assets',
      status: 'operational',
      description: 'Content delivery and static file serving',
      lastUpdated: '2024-01-15T10:30:00Z',
      uptime: '99.99%'
    }
  ]);

  const [incidents] = useState<Incident[]>([]);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'operational':
        return <CheckCircleIcon className="h-6 w-6 text-green-500" />;
      case 'degraded':
        return <ExclamationTriangleIcon className="h-6 w-6 text-yellow-500" />;
      case 'outage':
        return <XCircleIcon className="h-6 w-6 text-red-500" />;
      case 'maintenance':
        return <ClockIcon className="h-6 w-6 text-blue-500" />;
      default:
        return <CheckCircleIcon className="h-6 w-6 text-gray-500" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'operational':
        return 'bg-green-100 text-green-800';
      case 'degraded':
        return 'bg-yellow-100 text-yellow-800';
      case 'outage':
        return 'bg-red-100 text-red-800';
      case 'maintenance':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical':
        return 'bg-red-100 text-red-800';
      case 'major':
        return 'bg-orange-100 text-orange-800';
      case 'minor':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000));
    setLastRefresh(new Date());
    setIsRefreshing(false);
  };

  const overallStatus = services.every(service => service.status === 'operational') 
    ? 'operational' 
    : services.some(service => service.status === 'outage') 
    ? 'outage' 
    : 'degraded';

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <section className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">System Status</h1>
              <p className="text-gray-600 mt-2">
                Real-time status of TrendBeat services and infrastructure
              </p>
            </div>
            <div className="flex items-center space-x-4">
              <div className="text-sm text-gray-500">
                Last updated: {lastRefresh.toLocaleTimeString()}
              </div>
              <button
                onClick={handleRefresh}
                disabled={isRefreshing}
                className="inline-flex items-center px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 transition-colors"
              >
                <ArrowPathIcon className={`h-4 w-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
                {isRefreshing ? 'Refreshing...' : 'Refresh'}
              </button>
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Overall Status */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-8"
        >
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                {getStatusIcon(overallStatus)}
                <div className="ml-4">
                  <h2 className="text-xl font-semibold text-gray-900">
                    All Systems Operational
                  </h2>
                  <p className="text-gray-600">
                    All services are running normally
                  </p>
                </div>
              </div>
              <div className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(overallStatus)}`}>
                {overallStatus.charAt(0).toUpperCase() + overallStatus.slice(1)}
              </div>
            </div>
          </div>
        </motion.div>

        {/* Services Status */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="mb-8"
        >
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Service Status</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {services.map((service, index) => (
              <motion.div
                key={service.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 + index * 0.05 }}
                className="bg-white rounded-lg shadow-sm border border-gray-200 p-4"
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start">
                    {getStatusIcon(service.status)}
                    <div className="ml-3">
                      <h4 className="font-medium text-gray-900">{service.name}</h4>
                      <p className="text-sm text-gray-600">{service.description}</p>
                      {service.uptime && (
                        <p className="text-xs text-gray-500 mt-1">Uptime: {service.uptime}</p>
                      )}
                    </div>
                  </div>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(service.status)}`}>
                    {service.status}
                  </span>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Recent Incidents */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="mb-8"
        >
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Incidents</h3>
          {incidents.length === 0 ? (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
              <CheckCircleIcon className="h-12 w-12 text-green-500 mx-auto mb-4" />
              <h4 className="text-lg font-medium text-gray-900 mb-2">No Recent Incidents</h4>
              <p className="text-gray-600">
                We haven't experienced any service disruptions recently. All systems are running smoothly.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {incidents.map((incident) => (
                <div key={incident.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h4 className="text-lg font-medium text-gray-900">{incident.title}</h4>
                      <p className="text-gray-600">{incident.description}</p>
                    </div>
                    <div className="flex space-x-2">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getSeverityColor(incident.severity)}`}>
                        {incident.severity}
                      </span>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(incident.status)}`}>
                        {incident.status}
                      </span>
                    </div>
                  </div>
                  <div className="text-sm text-gray-500">
                    Started: {new Date(incident.startedAt).toLocaleString()}
                    {incident.resolvedAt && (
                      <span className="ml-4">
                        Resolved: {new Date(incident.resolvedAt).toLocaleString()}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </motion.div>


        {/* Footer */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
        >
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Status Page Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm text-gray-600">
            <div>
              <h4 className="font-medium text-gray-900 mb-2">Monitoring</h4>
              <p>We continuously monitor all our services 24/7 using industry-leading monitoring tools.</p>
            </div>
            <div>
              <h4 className="font-medium text-gray-900 mb-2">Updates</h4>
              <p>This page is automatically updated every minute. You can also subscribe to status updates via email.</p>
            </div>
            <div>
              <h4 className="font-medium text-gray-900 mb-2">Incidents</h4>
              <p>When incidents occur, we'll post updates here with details about the issue and our progress.</p>
            </div>
            <div>
              <h4 className="font-medium text-gray-900 mb-2">Contact</h4>
              <p>For urgent issues, please contact our support team at support@trendbeat.twokai.com</p>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default StatusPage;
